import React, { useMemo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import Link from '~/components/SsrLink';
import type { CacheReducedBlogPost } from '~/services/contentful/model/ContentfulBlogPost';
import { useBlogPreviewQuery } from '~/utils/hooks';
import {
	Post,
	PostData,
	PostReadMore,
	PostReadMoreWrapper,
	PostSummary,
	PostThumbnail,
	PostTitle,
} from './ReducedBlogPost.styles';

const MAX_TEXT_LENGTH = 100;

const truncate = (text?: string) => {
	if (!text || text.length < MAX_TEXT_LENGTH) {
		return text;
	}

	const words = text.substring(0, MAX_TEXT_LENGTH).split(' ');
	words.pop();
	return `${words.join(' ')}...`;
};

const ReducedBlogPost = ({
	imageSizes,
	onClick,
	post,
	shouldShowSummary,
}: Props) => {
	const { t } = useTranslation('blog');
	const getUrlWithPreviewQuery = useBlogPreviewQuery();

	const { coverImage, summary, title, slug } = post;

	const limitedSummary = useMemo(() => truncate(summary), [summary]);
	const limitedTitle = useMemo(() => truncate(title), [title]);

	return (
		<Link key={slug} href={getUrlWithPreviewQuery(`/blog/${slug}`)} passHref>
			<Post onClick={onClick}>
				<PostThumbnail>
					{coverImage?.url && (
						<Image
							src={coverImage.url}
							alt={coverImage.description}
							layout="fill"
							objectFit="cover"
							sizes={imageSizes}
						/>
					)}
				</PostThumbnail>
				<PostData>
					<PostTitle>{limitedTitle}</PostTitle>
					{shouldShowSummary && <PostSummary>{limitedSummary}</PostSummary>}
					<PostReadMoreWrapper>
						<PostReadMore>{t('blog.overview.readMore')}</PostReadMore>
					</PostReadMoreWrapper>
				</PostData>
			</Post>
		</Link>
	);
};

type Props = {
	imageSizes: string;
	onClick: (e: any) => void | undefined;
	post: CacheReducedBlogPost;
	shouldShowSummary?: boolean;
};

export default ReducedBlogPost;
